'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import HeroSection from '@/components/HeroSection'
import AmbassadorsSection from '@/components/AmbassadorsSection'
import VenueSection from '@/components/VenueSection'
import RegistrationForm from '@/components/RegistrationForm'
import ContactForm from '@/components/ContactForm'
import logo from '@/assets/logo-texto-blanco.png'

export default function App() {
  

  return (
    <div className="min-h-screen bg-black">
      <HeroSection />

      <motion.div
        className="relative z-10"
        transition={{ duration: 0.5 }}
      >
        {/* Navigation Bar */}
        <motion.nav
          className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-sm border-b border-gray-800"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <motion.div
                className="text-2xl text-white"
                whileHover={{ scale: 1.05 }}
              >
                <picture>
                  <img src={logo} alt="Liga Elite Logo" className="max-h-16 w-auto" />
                </picture>
              </motion.div>
              
              <div className="hidden md:flex items-center space-x-8">
                {[
                  { name: 'Embajadores', href: '#embajadores' },
                  { name: 'Sede', href: '#sede' },
                  { name: 'Registro', href: '#registro' },
                  { name: 'Contacto', href: '#contacto' }
                ].map((item, index) => (
                  <motion.a
                    key={item.name}
                    href={item.href}
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                  >
                    {item.name}
                  </motion.a>
                ))}
              </div>

              <motion.button
                className="bg-yellow-400 text-black px-6 py-2 rounded-full hover:bg-yellow-500 transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                Registrarse
              </motion.button>
            </div>
          </div>
        </motion.nav>

        {/* Main sections */}
        <main className="pt-20">
          <div id="embajadores">
            <AmbassadorsSection />
          </div>
          
          <div id="sede">
            <VenueSection />
          </div>
          
          <div id="registro">
            <RegistrationForm />
          </div>
          
          <div id="contacto">
            <ContactForm />
          </div>
        </main>

        {/* Footer */}
        <motion.footer
          className="bg-black py-12 border-t border-gray-800"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-2xl text-white mb-4">
                  LIGA <span className="text-yellow-400">RIVIERA</span>
                </h3>
                <p className="text-gray-400 mb-4">
                  La primera liga profesional de football americano en el Caribe Mexicano.
                </p>
                <div className="flex space-x-4">
                  {['📘', '📷', '🐦', '📺'].map((icon, index) => (
                    <motion.button
                      key={index}
                      className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-yellow-400 transition-colors duration-300"
                      whileHover={{ scale: 1.1 }}
                    >
                      {icon}
                    </motion.button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xl text-white mb-4">Enlaces Rápidos</h4>
                <ul className="space-y-2">
                  {[
                    'Sobre Nosotros',
                    'Equipos',
                    'Calendario',
                    'Estadísticas',
                    'Noticias'
                  ].map((link) => (
                    <li key={link}>
                      <motion.a
                        href="#"
                        className="text-gray-400 hover:text-yellow-400 transition-colors duration-300"
                        whileHover={{ x: 5 }}
                      >
                        {link}
                      </motion.a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl text-white mb-4">Información del Evento</h4>
                <div className="space-y-3 text-gray-400">
                  <p>📅 4-7 Diciembre 2024</p>
                  <p>📍 Cancún, Quintana Roo</p>
                  <p>📧 info@ligaelite.mx</p>
                  <p>📱 +52 998 123 4567</p>
                </div>
              </div>
            </div>

            <motion.div
              className="border-t border-gray-800 mt-8 pt-8 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <p className="text-gray-400">
                © 2025 Liga Riviera Flag. Todos los derechos reservados. 
                <span className="text-yellow-400"> Hecho con 🏈 en México por Let it Site</span>
              </p>
            </motion.div>
          </div>
        </motion.footer>

        {/* Floating action button */}
        <motion.button
          className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center text-2xl shadow-2xl z-30"
          whileHover={{ 
            scale: 1.1,
            boxShadow: '0 10px 30px rgba(255, 196, 0, 0.4)'
          }}
          whileTap={{ scale: 0.95 }}
          animate={{
            y: [0, -10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          ⬆️
        </motion.button>
      </motion.div>
    </div>
  )
}